import { CSSObject } from "@emotion/styled";
import {
  margin as polishedMargin,
  padding as polishedPadding,
  size as polishedSize,
} from "polished";

import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import type {
  ButtonModifiers,
  ButtonModifierOptions,
  ButtonSize,
} from "@brexhq/next/src/components/shared/Button/types";
import type {
  ButtonMargins,
  ResponsiveButtonSizes,
} from "@brexhq/next/src/components/shared/Link/types";
import { defaultTheme } from "@brexhq/shared/styles/theming";
import {
  isDarkTheme,
  type ModuleThemeType,
} from "@brexhq/shared/types/ModuleTheme";

const defaultMargin: ButtonMargins = [defaultTheme.spacing.p24, 0];

const getResponsiveRules = (
  size: ButtonSize,
  isLink: boolean | undefined,
): CSSObject => {
  switch (size) {
    case "lrg":
      return {
        ...(isLink
          ? {}
          : { minWidth: 160, height: 48, ...polishedPadding(0, 20) }),
        fontSize: "14px",
        lineHeight: "22px",
      };
    case "DEVIATED_NAV_SIZE":
      return {
        ...(isLink
          ? {}
          : {
              minWidth: 148,
              height: 48,
              ...polishedPadding(0, 12),
              ...polishedSize(36, "initial"),
            }),
        fontSize: "14px",
        lineHeight: "22px",
      };
    case "med":
      return {
        ...(isLink
          ? {}
          : { minWidth: 120, height: 40, ...polishedPadding(0, 12) }),
        fontSize: "14px",
        lineHeight: "22px",
      };
    case "sml":
      return {
        ...(isLink
          ? {}
          : { minWidth: 112, height: 32, ...polishedPadding(0, 12) }),
        fontSize: "12px",
        lineHeight: "18px",
      };
  }
};

export const getModifiedStyles = (
  modifier: Exclude<ButtonModifiers, "unstyled">,
  theme: ModuleThemeType = "PrimaryWhite",
): ButtonModifierOptions => {
  const { colors: libraryColors } = defaultTheme;

  const {
    standard: { textPrimary },
  } = getModuleThemeColors(theme);
  const primaryOutlineColor = textPrimary;

  const hoverColorBG = isDarkTheme(theme)
    ? libraryColors.background.primary
    : libraryColors.background.inverse.primary;
  const hoverColorText = isDarkTheme(theme)
    ? libraryColors.background.inverse.primary
    : libraryColors.background.primary;
  switch (modifier) {
    case "primary":
      return {
        backgroundColor: libraryColors.accent.primary.background.default,
        borderColor: libraryColors.accent.primary.background.default,
        textColor: libraryColors.content.inverse.primary,
        hoverTextColor: libraryColors.content.inverse.primary,
        hoverBorderColor: libraryColors.accent.primary.background.hover,
        hoverBackgroundColor: libraryColors.accent.primary.background.hover,
        focusTextColor: libraryColors.content.inverse.primary,
        focusOutline: libraryColors.accent.primary.background.default,
        focusBorderColor: libraryColors.accent.primary.background.default,
        focusBackgroundColor: "transparent",
        activeBackgroundColor: libraryColors.accent.primary.background.pressed,
        activeTextColor: libraryColors.accent.primary.background.default,
        activeBorderColor: libraryColors.accent.primary.background.pressed,
        iconColor: "inherit",
        hoverIconColor: libraryColors.content.primary,
      };

    case "secondary":
      return {
        borderColor: libraryColors.border.default,
        backgroundColor: libraryColors.background.primary,
        textColor: libraryColors.content.primary,
        hoverTextColor: libraryColors.content.primary,
        hoverBackgroundColor: libraryColors.background.primary,
        hoverBorderColor: libraryColors.border.hover,
        focusBorderColor: libraryColors.border.focused,
        focusTextColor: libraryColors.accent.primary.background.default,
        focusBackgroundColor: "transparent",
        focusOutline: "none",
        activeBackgroundColor: libraryColors.background.primary,
        activeBorderColor: libraryColors.border.pressed,
        activeTextColor: libraryColors.accent.primary.background.default,
        iconColor: libraryColors.accent.primary.background.default,
        hoverIconColor: libraryColors.accent.primary.background.default,
      };
    case "outline":
      return {
        borderColor: primaryOutlineColor,
        activeBorderColor: primaryOutlineColor,
        hoverBorderColor: "transparent",
        focusBorderColor: primaryOutlineColor,
        textColor: primaryOutlineColor,
        activeTextColor: hoverColorText,
        hoverTextColor: hoverColorText,
        focusTextColor: hoverColorText,
        backgroundColor: "transparent",
        activeBackgroundColor: hoverColorBG,
        hoverBackgroundColor: hoverColorBG,
        focusBackgroundColor: hoverColorBG,
        focusOutline: primaryOutlineColor,
        iconColor: "inherit",
        hoverIconColor: hoverColorBG,
      };
    case "legacyLink":
      return {
        borderColor: "transparent",
        activeBorderColor: "transparent",
        hoverBorderColor: "transparent",
        focusBorderColor: "transparent",
        textColor: libraryColors.accent.primary.background.default,
        activeTextColor: libraryColors.accent.primary.background.pressed,
        hoverTextColor: libraryColors.accent.primary.background.hover,
        focusTextColor: libraryColors.accent.primary.background.hover,
        backgroundColor: "transparent",
        activeBackgroundColor: "transparent",
        hoverBackgroundColor: "transparent",
        focusBackgroundColor: "transparent",
        focusOutline: libraryColors.accent.primary.background.default,
        iconColor: libraryColors.accent.primary.background.default,
        hoverIconColor: libraryColors.accent.primary.background.hover,
        isLink: true,
      };
  }
};

export const lrgButtons: ResponsiveButtonSizes = {
  LRG: "lrg",
  MED: "lrg",
  SML: "lrg",
};
export const medButtons: ResponsiveButtonSizes = {
  LRG: "med",
  MED: "med",
  SML: "med",
};
export const smlButtons: ResponsiveButtonSizes = {
  LRG: "sml",
  MED: "sml",
  SML: "sml",
};

interface ButtonTheme {
  modifier: Exclude<ButtonModifiers, "unstyled">;
  margin: ButtonMargins;
  padding?: ButtonMargins;
  size: ResponsiveButtonSizes;
  isFullWidth: boolean;
  // Escape hatches, try not to use
  smallBreakpointOverrides?: CSSObject;
  mediumBreakpointOverrides?: CSSObject;
  largeBreakpointOverrides?: CSSObject;
  theme?: ModuleThemeType;
  disabled?: boolean;
  withIcon?: boolean;
}

const defaultSize: ResponsiveButtonSizes = {
  LRG: "lrg",
  MED: "lrg",
  SML: "lrg",
};

export const createButtonStyles = (
  {
    modifier = "primary",
    margin = defaultMargin,
    size = defaultSize,
    isFullWidth = false,
    theme,
    disabled,
    withIcon,
  }: {
    modifier?: ButtonModifiers;
    margin?: ButtonMargins;
    padding?: ButtonMargins;
    size?: ResponsiveButtonSizes;
    isFullWidth?: boolean;
    theme?: ModuleThemeType;
    disabled?: boolean;
    withIcon?: boolean;
  } = { theme: "PrimaryWhite" },
): CSSObject => {
  if (modifier === "unstyled")
    return {
      padding: 0,
      border: "none",
      background: "none",
      "&:focus": {
        outline: "none",
      },
      textAlign: "unset",
      width: "100%",
    };

  return createBaseButtonStyles({
    modifier,
    margin,
    size,
    isFullWidth,
    theme,
    disabled,
    withIcon,
  });
};

// this fn should not be exported
const createBaseButtonStyles = ({
  size,
  isFullWidth,
  margin,
  modifier,
  padding,
  smallBreakpointOverrides = {},
  mediumBreakpointOverrides = {},
  largeBreakpointOverrides = {},
  theme,
  disabled,
  withIcon,
}: ButtonTheme): CSSObject => {
  const modifiedStyles = getModifiedStyles(modifier, theme);
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: isFullWidth ? "100%" : "max-content",
    ...(withIcon && {
      gap: defaultTheme.spacing.p8,
    }),

    // Text
    color: modifiedStyles.textColor,
    textDecoration: "none",
    fontFamily: defaultTheme.rebirth.fonts.family,
    fontVariationSettings: '"wght" 500',
    whiteSpace: "nowrap",

    opacity: disabled ? 0.65 : "initial",

    // Styles
    background: "none",
    backgroundColor: modifiedStyles.backgroundColor,
    borderRadius: defaultTheme.borders.radius.default,
    transition: defaultTheme.transitions.defaultTransition,
    border: modifiedStyles.isLink
      ? "none"
      : `1px solid ${modifiedStyles.borderColor}`,

    "& > svg": {
      color: modifiedStyles.iconColor,
    },

    // Spacing
    // TODO - should this be responsive?
    ...(modifiedStyles.isLink ? {} : polishedMargin(...margin)),

    // States
    "&:hover": disabled
      ? {}
      : {
          color: modifiedStyles.hoverTextColor,
          backgroundColor: modifiedStyles.hoverBackgroundColor,
          borderColor: modifiedStyles.hoverBorderColor,
          textDecoration: "none",
          svg: {
            color: modifiedStyles.hoverIconColor,
          },
        },

    "&:active": disabled
      ? {}
      : {
          color: modifiedStyles.activeTextColor,
          backgroundColor: modifiedStyles.activeBackgroundColor,
          borderColor: modifiedStyles.activeBorderColor,
          textDecoration: "none",
        },

    "&:focus , &:focus-visible": {
      outlineOffset: "2px",
      textDecoration: "none",
      outline: `2px solid ${modifiedStyles.focusOutline}`,
      borderColor: modifiedStyles.focusBorderColor,
      color: modifiedStyles.focusTextColor,
      backgroundColor: modifiedStyles.hoverBackgroundColor,
    },
    ...getResponsiveRules(size.LRG, modifiedStyles.isLink),
    // LEGACY - if padding is passed in overwrite the button padding
    ...(padding && polishedPadding(...padding)),
    ...largeBreakpointOverrides,

    [defaultTheme.breakpoints.MED]: {
      ...getResponsiveRules(size.MED, modifiedStyles.isLink),
      ...mediumBreakpointOverrides,
    },

    [defaultTheme.breakpoints.SML]: {
      ...getResponsiveRules(size.SML, modifiedStyles.isLink),
      ...smallBreakpointOverrides,
    },
  };
};

export const createInlineSSUButtonStyles = () =>
  createBaseButtonStyles({
    theme: "PrimaryWhite",
    modifier: "primary",
    size: defaultSize,
    isFullWidth: false,
    padding: [0],
    margin: [0],
    mediumBreakpointOverrides: {
      height: 36,
      minWidth: 130,
      padding: 0,
    },
    smallBreakpointOverrides: {
      minWidth: 36,
      height: 36,
      padding: 0,
    },
  });
